/**
 * The VR interface's popup panel (vrInterface.ts): a hotspot's, model's or overlay's content on a
 * panel in front of the visitor. Text (scrolled with a stick or by dragging), a photo gallery, a
 * video on its own quad over the media area, PDF pages (pdf.js through the viewer's loader), media
 * tabs when there are several, and "Open after VR" for a link or web page.
 */
import * as pc from 'playcanvas';
import { VR_DRAW_ORDER, VrPanel, vrUiLayer } from './vrPanel';
import { VR_POPUP_METRES, VR_POPUP_PIXELS, VR_POPUP_TEXT, layoutVrPopup, paintVrPopup, popupTextHeight, type HitRegion, type VrMediaKind, type VrPanelTheme, type VrPopupLayout, type VrPopupSpec } from './vrPanelKit';
import { vrPopupContent, type VrLabels, type VrPopupContent, type VrPopupSource } from './vrMenus';
import type { VrInterfaceHost } from './vrInterface';

/**
 * A popup panel (hotspot popups, and overlays): text, then a photo gallery, a video or PDF pages,
 * and "Open after VR" for a link.
 */
export class VrPopupPanel {
    readonly panel: VrPanel;
    content: VrPopupContent | null = null;
    source: VrPopupSource | null = null;
    /** The overlay it shows (overlay panels). */
    overlayId: string | null = null;
    overlayDismissible = true;
    private media: VrMediaKind | null = null;
    private imageIndex = 0;
    private pdfPage = 0;
    private pdfCount = 0;
    private textScroll = 0;
    private textHeight = 0;
    layout: VrPopupLayout | null = null;
    hoverId: string | null = null;
    dirty = false;
    private readonly images = new Map<string, HTMLImageElement | 'error'>();
    private pdfDoc: any = null;
    private pdfUrl: string | null = null;
    private pdfCanvas: HTMLCanvasElement | null = null;
    private pdfError = false;
    private pdfRenderToken = 0;
    private video: HTMLVideoElement | null = null;
    private videoError = false;
    private videoEntity: pc.Entity | null = null;
    private videoTexture: pc.Texture | null = null;
    private videoMaterial: pc.StandardMaterial | null = null;

    constructor(private readonly app: pc.AppBase, name: string, parent: pc.Entity, private readonly host: VrInterfaceHost, private readonly theme: () => VrPanelTheme, private readonly labels: () => VrLabels, private readonly isSaved: (url: string) => boolean, order: number) {
        this.panel = new VrPanel(app, name, VR_POPUP_METRES, VR_POPUP_PIXELS, parent, order);
    }

    get visible(): boolean {
        return this.panel.visible;
    }

    show(source: VrPopupSource): void {
        this.stopVideo();
        this.source = source;
        this.content = vrPopupContent(source);
        const c = this.content;
        this.media = c.images.length ? 'image' : c.video ? 'video' : c.pdf ? 'pdf' : null;
        this.imageIndex = 0;
        this.pdfPage = 0;
        this.textScroll = 0;
        this.panel.visible = true;
        this.openMedia();
        this.dirty = true;
    }

    hide(): void {
        this.stopVideo();
        this.panel.visible = false;
        this.content = null;
        this.source = null;
        this.overlayId = null;
    }

    private mediaKinds(): VrMediaKind[] {
        const c = this.content;
        if (!c) return [];
        const kinds: VrMediaKind[] = [];
        if (c.images.length) kinds.push('image');
        if (c.video) kinds.push('video');
        if (c.pdf) kinds.push('pdf');
        return kinds;
    }

    private openMedia(): void {
        const c = this.content;
        if (!c) return;
        if (this.media === 'image') this.loadImage(c.images[this.imageIndex]);
        if (this.media === 'video' && c.video) this.startVideo(c.video);
        if (this.media === 'pdf' && c.pdf) void this.loadPdf(c.pdf);
        this.syncVideoQuad();
    }

    private loadImage(url: string | undefined): void {
        if (!url || this.images.has(url)) return;
        const image = new Image();
        image.crossOrigin = 'anonymous';
        image.onload = () => { this.images.set(url, image); this.dirty = true; this.host.requestRender('vr-popup'); };
        image.onerror = () => { this.images.set(url, 'error'); this.dirty = true; };
        image.src = url;
    }

    private async loadPdf(url: string): Promise<void> {
        if (this.pdfUrl === url && (this.pdfDoc || this.pdfError)) {
            void this.renderPdfPage();
            return;
        }
        this.pdfUrl = url;
        this.pdfDoc = null;
        this.pdfError = false;
        this.pdfCount = 0;
        try {
            const lib = await this.host.loadPdfJs();
            const doc = await lib.getDocument(url).promise;
            if (this.pdfUrl !== url) return;
            this.pdfDoc = doc;
            this.pdfCount = doc.numPages;
            await this.renderPdfPage();
        } catch {
            if (this.pdfUrl === url) {
                this.pdfError = true;
                this.dirty = true;
            }
        }
    }

    private async renderPdfPage(): Promise<void> {
        const doc = this.pdfDoc;
        const rect = this.layout?.mediaRect ?? { w: 700, h: 560 };
        if (!doc) return;
        const token = ++this.pdfRenderToken;
        const page = await doc.getPage(this.pdfPage + 1);
        const base = page.getViewport({ scale: 1 });
        // Twice the media area's pixels, so text stays sharp when the visitor leans in.
        const scale = Math.min((rect.w * 2) / base.width, (rect.h * 2) / base.height);
        const viewport = page.getViewport({ scale });
        const canvas = this.pdfCanvas ??= document.createElement('canvas');
        canvas.width = Math.floor(viewport.width);
        canvas.height = Math.floor(viewport.height);
        const ctx = canvas.getContext('2d');
        if (!ctx) return;
        ctx.fillStyle = '#ffffff';
        ctx.fillRect(0, 0, canvas.width, canvas.height);
        await page.render({ canvasContext: ctx, viewport }).promise;
        if (token !== this.pdfRenderToken) return;
        this.dirty = true;
        this.host.requestRender('vr-popup');
    }

    private startVideo(url: string): void {
        this.videoError = false;
        const video = document.createElement('video');
        video.crossOrigin = 'anonymous';
        video.playsInline = true;
        video.loop = true;
        video.preload = 'auto';
        video.src = url;
        video.addEventListener('error', () => { this.videoError = true; this.dirty = true; this.syncVideoQuad(); });
        video.addEventListener('loadeddata', () => { this.syncVideoQuad(); this.dirty = true; });
        video.addEventListener('play', () => { this.dirty = true; });
        video.addEventListener('pause', () => { this.dirty = true; });
        this.video = video;
        // A select is a user gesture: playing with sound is allowed. If the browser still says no,
        // the Play button stays up.
        video.play().catch(() => { this.dirty = true; });
    }

    private stopVideo(): void {
        if (this.video) {
            this.video.pause();
            this.video.removeAttribute('src');
            this.video.load();
            this.video = null;
        }
        if (this.videoEntity) this.videoEntity.enabled = false;
    }

    /** The video's own quad over the media area, sized to the video's shape. */
    private syncVideoQuad(): void {
        const video = this.video;
        const rect = this.layout?.mediaRect;
        const show = this.media === 'video' && !!video && !this.videoError && video.videoWidth > 0 && !!rect && this.panel.visible;
        if (!show) {
            if (this.videoEntity) this.videoEntity.enabled = false;
            return;
        }
        if (!this.videoEntity) {
            const device = this.app.graphicsDevice;
            this.videoTexture = new pc.Texture(device, { name: 'vr-popup-video', format: pc.PIXELFORMAT_RGBA8, mipmaps: false, minFilter: pc.FILTER_LINEAR, magFilter: pc.FILTER_LINEAR, addressU: pc.ADDRESS_CLAMP_TO_EDGE, addressV: pc.ADDRESS_CLAMP_TO_EDGE });
            const material = new pc.StandardMaterial();
            material.diffuse = new pc.Color(0, 0, 0);
            material.emissive = new pc.Color(1, 1, 1);
            material.emissiveMap = this.videoTexture;
            material.useLighting = false;
            material.useFog = false;
            material.useSkybox = false;
            material.useTonemap = false;
            // Blended like the panels, so it draws in the same (transparent) pass after its panel;
            // an opaque material would draw first and the panel would cover it.
            material.blendType = pc.BLEND_NORMAL;
            material.depthTest = false;
            material.depthWrite = false;
            material.cull = pc.CULLFACE_NONE;
            material.update();
            this.videoMaterial = material;
            const mesh = new pc.Mesh(device);
            mesh.setPositions([-0.5, -0.5, 0, 0.5, -0.5, 0, 0.5, 0.5, 0, -0.5, 0.5, 0]);
            mesh.setUvs(0, [0, 1, 1, 1, 1, 0, 0, 0]);
            mesh.setIndices([0, 1, 2, 0, 2, 3]);
            mesh.update();
            const instance = new pc.MeshInstance(mesh, material);
            instance.drawOrder = VR_DRAW_ORDER.video;
            this.videoEntity = new pc.Entity('vr-popup-video');
            this.videoEntity.addComponent('render', { meshInstances: [instance], layers: [vrUiLayer(this.app).id], castShadows: false, receiveShadows: false });
            this.panel.entity.addChild(this.videoEntity);
        }
        this.videoTexture!.setSource(video!);
        const { width: W, height: H } = VR_POPUP_PIXELS;
        const scale = Math.min(rect!.w / video!.videoWidth, rect!.h / video!.videoHeight);
        const w = video!.videoWidth * scale, h = video!.videoHeight * scale;
        this.videoEntity.setLocalPosition((rect!.x + rect!.w / 2) / W - 0.5, 0.5 - (rect!.y + rect!.h / 2) / H, 0);
        this.videoEntity.setLocalScale(w / W, h / H, 1);
        this.videoEntity.enabled = true;
    }

    /** Per frame: a playing video's next frame. */
    update(): void {
        if (this.video && !this.video.paused && this.videoEntity?.enabled && this.videoTexture) {
            this.videoTexture.upload();
            this.host.requestRender('vr-video');
        }
    }

    scroll(by: number): boolean {
        const rect = this.layout?.textRect;
        if (!rect || this.textHeight <= rect.h) return false;
        const next = Math.min(Math.max(0, this.textScroll + by), this.textHeight - rect.h);
        if (next === this.textScroll) return false;
        this.textScroll = next;
        this.dirty = true;
        return true;
    }

    get scrollPosition(): number {
        return this.textScroll;
    }

    setScroll(value: number): void {
        const rect = this.layout?.textRect;
        if (!rect) return;
        const next = Math.min(Math.max(0, value), Math.max(0, this.textHeight - rect.h));
        if (next !== this.textScroll) {
            this.textScroll = next;
            this.dirty = true;
        }
    }

    regions(): readonly HitRegion[] {
        return this.layout?.regions ?? [];
    }

    /** Run a region's action; true when the popup handled it. */
    run(id: string): boolean {
        const c = this.content;
        if (!c) return false;
        if (id.startsWith('tab:')) {
            const kind = id.slice(4) as VrMediaKind;
            if (kind !== this.media) {
                this.stopVideo();
                this.media = kind;
                this.openMedia();
                this.dirty = true;
            }
            return true;
        }
        if (id === 'prev' || id === 'next') {
            const step = id === 'next' ? 1 : -1;
            if (this.media === 'image') {
                this.imageIndex = Math.min(Math.max(0, this.imageIndex + step), c.images.length - 1);
                this.loadImage(c.images[this.imageIndex]);
            } else if (this.media === 'pdf' && this.pdfCount) {
                this.pdfPage = Math.min(Math.max(0, this.pdfPage + step), this.pdfCount - 1);
                void this.renderPdfPage();
            }
            this.dirty = true;
            return true;
        }
        if (id === 'play' && this.video) {
            if (this.video.paused) this.video.play().catch(() => undefined);
            else this.video.pause();
            this.dirty = true;
            return true;
        }
        return false;
    }

    /** Redraw the canvas when something changed. */
    draw(): void {
        if (!this.dirty || !this.panel.visible || !this.content || !this.panel.ctx) return;
        this.dirty = false;
        const ctx = this.panel.ctx;
        const c = this.content;
        const labels = this.labels();
        const kinds = this.mediaKinds();
        const media = this.media;
        const page = media === 'image' && c.images.length > 1
            ? { index: this.imageIndex, count: c.images.length }
            : media === 'pdf' && this.pdfCount > 1 ? { index: this.pdfPage, count: this.pdfCount } : null;
        const note = c.model ? labels.modelOutsideVr : undefined;
        const spec: VrPopupSpec = {
            title: c.title,
            text: c.text,
            media: kinds,
            current: media,
            page,
            playing: media === 'video' && this.video && !this.videoError ? !this.video.paused : null,
            link: c.link ? { label: labels.saveLink, saved: this.isSaved(c.link.url) } : null,
            note,
            closable: this.overlayId === null || this.overlayDismissible,
            background: this.source?.backgroundColor,
            color: this.source?.textColor,
        };
        // The text's height at its column width, for scrolling (measured at the paint font).
        const probe = layoutVrPopup(spec);
        ctx.font = `${VR_POPUP_TEXT.font}px "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif`;
        const body = [c.text, note ? `\n${note}` : ''].filter(Boolean).join('\n');
        this.textHeight = popupTextHeight((s) => ctx.measureText(s).width, body, probe.textRect.w);
        const layout = layoutVrPopup({ ...spec, scroll: this.textScroll }, VR_POPUP_PIXELS, this.textHeight);
        this.textScroll = layout.scroll;
        this.layout = layout;
        // Media buttons' words come from the label overrides.
        for (const tab of layout.tabs) tab.label = tab.id === 'tab:image' ? labels.photos : tab.id === 'tab:video' ? labels.video : labels.pdf;
        for (const b of layout.buttons) if (b.id === 'play') b.label = this.video?.paused === false ? labels.pause : labels.play;
        for (const b of layout.buttons) if (b.id === 'save-link' && spec.link?.saved) b.label = `${labels.linkSaved} ✓`;
        let mediaSource: (CanvasImageSource & { width: number; height: number }) | null = null;
        let message: string | null = null;
        if (media === 'image') {
            const img = this.images.get(c.images[this.imageIndex]);
            if (img && img !== 'error') mediaSource = img;
            else message = img === 'error' ? '✕' : '…';
        } else if (media === 'pdf') {
            if (this.pdfError) message = '✕ PDF';
            else if (this.pdfCanvas && this.pdfDoc) mediaSource = this.pdfCanvas;
            else message = '…';
        } else if (media === 'video') {
            message = this.videoError ? '✕' : this.video && this.video.videoWidth ? null : '…';
        }
        paintVrPopup(ctx, layout, spec, this.theme(), this.hoverId, mediaSource, message);
        this.panel.commit();
        this.syncVideoQuad();
    }

    /** For tests and debugging: what the popup shows. */
    debug() {
        const c = this.content;
        return {
            title: c?.title ?? '',
            media: this.media,
            image: this.imageIndex,
            images: c?.images.length ?? 0,
            pdfPage: this.pdfPage,
            pdfPages: this.pdfCount,
            pdfRendered: !!this.pdfCanvas && !!this.pdfDoc,
            playing: !!this.video && !this.video.paused,
            videoTime: this.video?.currentTime ?? 0,
            videoShown: !!this.videoEntity?.enabled,
            link: c?.link?.url ?? null,
            overlay: this.overlayId,
        };
    }

    destroy(): void {
        this.stopVideo();
        this.videoEntity?.destroy();
        this.videoTexture?.destroy();
        this.videoMaterial?.destroy();
        this.panel.destroy();
    }
}
